import { FilterTag } from "@volkswagen-onehub/components-core";
import React from "react";
import { useLocation } from "react-router-dom";
import useFiltersQuery from "../api/use-filters-query";
import { useAppliedFilterStore } from "../store/filtersv2";
import { getLang } from "../translations";
import { FiltersResponse, FiltersResponseKey, Language } from "../types";
import { Filters } from "../types/filters";
import { useFeatureServices } from "../use-feature-services";
import { getSingleSelectedDealer } from "../utils/get-single-selected-dealer";
import { createConfiguration, createConstantData, createPartner } from "../utils/get-tracking-event-data";
import { useFiltering } from "../utils/use-filtering";

export const QuickFilters = () => {
  const lang = getLang();
  const location = useLocation();
  const { tracking } = useFeatureServices();
  const { isLoading, data } = useFiltersQuery<FiltersResponse, FiltersResponse>();
  const { applied } = useAppliedFilterStore();
  const { applyQuickFilter } = useFiltering();

  const partner = getSingleSelectedDealer();

  const getQuickFilters = (data: FiltersResponse) => {
    const quickFilters = [];
    quickFilters.push({keyName: FiltersResponseKey.CATEGORY, ...data?.[FiltersResponseKey.CATEGORY].filter(option => option.value === "new")[0]});
    quickFilters.push({keyName: FiltersResponseKey.CATEGORY, ...data?.[FiltersResponseKey.CATEGORY].filter(option => option.value === "used")[0]});
    quickFilters.push({keyName: FiltersResponseKey.CATEGORY, ...data?.[FiltersResponseKey.CATEGORY].filter(option => option.value === "demo")[0]});
    quickFilters.push({keyName: FiltersResponseKey.IN_STOCK, ...data?.[FiltersResponseKey.IN_STOCK][0]});
    if (!location.pathname.includes("results")) {
      quickFilters.push({keyName: FiltersResponseKey.VEHICLE_TYPES, ...data?.[FiltersResponseKey.VEHICLE_TYPES].filter(option => option.value === "suv")[0]});
      quickFilters.push({keyName: FiltersResponseKey.VEHICLE_TYPES, ...data?.[FiltersResponseKey.VEHICLE_TYPES].filter(option => option.value === "electric")[0]});
      quickFilters.push({keyName: FiltersResponseKey.TRANSMISSION, ...data?.[FiltersResponseKey.TRANSMISSION].filter(option => option.value === "manual")[0]});
    }
    return quickFilters;
  };

  const handleClick = (keyName: FiltersResponseKey, label: string, value?: string) => {
    let updatedSelection;
    if (keyName === FiltersResponseKey.IN_STOCK) {
      applyQuickFilter("in_stock", !applied.in_stock);
    } else if (keyName === FiltersResponseKey.VEHICLE_TYPES) {
      const selection = applied.vehicle_type as string[] || [];
      updatedSelection = selection?.includes(value as string) ? selection.filter((item) => item !== value) : [...selection, value];
      applyQuickFilter("vehicle_type", updatedSelection);
    } else if (keyName === FiltersResponseKey.CATEGORY) {
      const selection = applied.category || [];
      if (value === "used" && selection?.includes("certified")) {
        updatedSelection = selection?.includes(value as string) ? selection.filter((item) => item !== value) : [
          ...selection.filter(item => item !== "certified"), value
        ];
      } else {
        updatedSelection = selection?.includes(value as string) ? selection.filter((item) => item !== value) : [...selection, value];
      }
      applyQuickFilter("category", updatedSelection);
    } else if (value) {
      const selection = applied[keyName as keyof Filters] as string[] || [];
      updatedSelection = selection?.includes(value) ? selection.filter((item) => item !== value) : [...selection, value];
      applyQuickFilter(keyName as keyof Filters, updatedSelection);
    }

    if (keyName === FiltersResponseKey.IN_STOCK && !applied.in_stock) {
      tracking.track("VWBasic_FilterSelect_Click", {
        ...createConstantData(),
        configuration: createConfiguration(),
        partner: createPartner(partner),
        link: {
          url: window.location.href,
          name: label
        },
        filter: {
          type: 'single_value',
          name: keyName,
          values: [!applied.in_stock],
        }
      });
    } else if (updatedSelection?.includes(value)) {
      tracking.track("VWBasic_FilterSelect_Click", {
        ...createConstantData(),
        configuration: createConfiguration(),
        partner: createPartner(partner),
        link: {
          url: window.location.href,
          name: label
        },
        filter: {
          type: 'multi_value',
          name: keyName,
          values: updatedSelection,
        }
      });
    }

  };

  const isActive = (keyName: FiltersResponseKey, value?: string) => {
    switch (keyName) {
      case FiltersResponseKey.IN_STOCK:
        return applied.in_stock;
      case FiltersResponseKey.VEHICLE_TYPES:
        return value ? applied.vehicle_type?.includes(value) : false;
      case FiltersResponseKey.CATEGORY:
      case FiltersResponseKey.VEHICLE_TYPES:
      case FiltersResponseKey.TRANSMISSION:
      default:
        return value ? (applied[keyName as keyof Filters] as string[])?.includes(value) : false;
    }
  };

  return (
    <>
      {
        isLoading
        ? <></>
        : getQuickFilters(data as FiltersResponse)?.map(({keyName, value, label}) => {
          return (
            <FilterTag
              key={value ? `${keyName}-${value}` : keyName}
              text={label[lang]}
              emphasis="secondary"
              active={isActive(keyName, value)}
              aria-pressed={isActive(keyName, value)}
              onClick={() => handleClick(keyName, label[Language.EN], value)}
            />
          )
     })}
    </>
  );
};
